import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ImageModal from "./components/ImageModal";
import altImage from "assets/altImage.png";
import {
  getUserProfile,
  updateUserProfile,
} from "store/services/Profile/profile";
import { setProfile } from "store/actions/Profile/profile";
import { showToast } from "utils/showToast";
import { useNavigate } from "react-router-dom";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import Loader from "components/Loader";
import { parsePhoneNumber } from "libphonenumber-js";

const ProfileUpdate = () => {
  const { selectedStore } = useSelector((store) => store.selectedStore);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((store) => store.userReducer);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userProfile, setUserProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [isProfileUpdated, setProfileUpdated] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    location: "",
    postcode: "",
    errors: {
      first_name: "",
      last_name: "",
      location: "",
      postcode: "",
      phone: "",
    },
  });
  const [phone, setPhone] = useState("");

  const handleChangePhoneNumber = (value) => {
    setPhone(value);
    setFormData((prevState) => ({
      ...prevState,
      errors: {
        ...prevState.errors,
        phone: "",
      },
    }));
    if (value) {
      try {
        const phoneNumber = parsePhoneNumber(value);
        const nationalNumber = phoneNumber.nationalNumber;
        const maxLength =
          phoneNumber.getType() === "MOBILE"
            ? phoneNumber.getCountry().maxLengthMobile
            : phoneNumber.getCountry().maxLengthFixed;

        if (nationalNumber.length > maxLength) {
          setFormData((prevState) => ({
            ...prevState,
            errors: {
              ...prevState.errors,
              phone: "Phone number is too long",
            },
          }));
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    if (userProfile) {
      setFormData({
        first_name: userProfile.first_name || "",
        last_name: userProfile.last_name || "",
        location: userProfile.location || "",
        postcode: userProfile.postcode || "",
        errors: {
          first_name: "",
          last_name: "",
          location: "",
          postcode: "",
          phone: "",
        },
      });
      setPhone(userProfile.phone_number || "");
    }
  }, [userProfile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prevState) => ({
      ...prevState,
      [name]: name === 'last_name' ?  value.replace(/[^a-zA-Z]/g, "").slice(0, 50): value,
      errors: {
        ...prevState.errors,
        [name]: "",
      },
    }));
  };

  const handleModalToggle = () => {
    setIsModalOpen(!isModalOpen);
  };

  const modalData = {
    title: "Change Photo",
    redButton: "Cancel",
    purpleButton: "CHANGE",
  };

  const checkProfileUpdate = (res) => {
    setProfileUpdated(res);
  };

  const handleSubmit = async () => {
    let hasErrors = false;
    const newErrors = {
      first_name: "",
      last_name: "",
      location: "",
      postcode: "",
      phone: "",
    };

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
      hasErrors = true;
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
      hasErrors = true;
    }
    if (!formData.location.trim()) {
      newErrors.location = "Address is required";
      hasErrors = true;
    }
    if (!formData.postcode.trim()) {
      newErrors.postcode = "Postcode is required";
      hasErrors = true;
    }
    if (!phone) {
      newErrors.phone = "Phone number is required";
      hasErrors = true;
    } else if (!isValidPhoneNumber(phone)) {
      newErrors.phone = "Please enter a valid phone number";
      hasErrors = true;
    }

    if (hasErrors) {
      setFormData((prevState) => ({
        ...prevState,
        errors: newErrors,
      }));
      return;
    }

    const result = await updateUserProfile(user.accessToken, user.data.id, {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      phone_number: phone.trim(),
      location: formData.location.trim(),
      postcode: formData.postcode.trim(),
    });

    if (result.success) {
      showToast(result.data, "success", true);
      navigate(`/users/${user.data.id}/profile`);
    }
  };

  const fetchUserProfile = async () => {
    try {
      if (user) {
        const profile = await getUserProfile(user.accessToken, user.data.id);
        if (profile.success) {
          setUserProfile(profile.data);
          dispatch(setProfile(profile.data));
          setProfileUpdated(false);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isProfileUpdated) {
      fetchUserProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProfileUpdated]);

  useEffect(() => {
    if (user) {
      fetchUserProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen ">
      {userProfile && (
        <div className="w-full mx-10 mt-8 font-bold image-wrapper">
          <div className="flex flex-col items-center justify-center">
            <img
              className="border rounded-full w-44 h-44"
              src={userProfile.photo_url ? userProfile.photo_url : altImage}
              alt={"ProfilePicture"}
            />
            <div
              style={{
                color: selectedStore?.theme.color,
              }}
              className="mt-2 hover:text-lighter-purple"
            >
              <button onClick={handleModalToggle}>Change Photo</button>
            </div>
          </div>
          <div className="items-center my-2 text-center text-gray-900">
            <h1>
              {userProfile.first_name
                ? userProfile.first_name + " " + userProfile.last_name
                : ""}
            </h1>
            <h1 className="text-gray-700">{userProfile.location}</h1>
            <h1 className="text-gray-700">{userProfile.email}</h1>
          </div>
          <div className="flex flex-col items-center justify-center mx-10 text-gray-900 ">
            <div className="w-full sm:w-1/2 md:w-1/3 lg:w-1/4">
              <label className="text-sm"> First Name </label>
              <div className="p-2 border border-gray-300 rounded-md">
                <input
                  className="w-full outline-none"
                  label="First Name"
                  placeholder="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>
              {formData.errors.first_name && (
                <p className="mt-1 text-xs text-red-500">
                  {formData.errors.first_name}
                </p>
              )}
            </div>
            <div className="w-full sm:w-1/2 md:w-1/3 lg:w-1/4">
              <label className="text-sm"> Last Name </label>
              <div className="p-2 border border-gray-300 rounded-md">
                <input
                  className="w-full outline-none"
                  label={"Last Name"}
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Last Name"
                />
              </div>
              {formData.errors.last_name && (
                <p className="mt-1 text-xs text-red-500">
                  {formData.errors.last_name}
                </p>
              )}
            </div>
            <div className="w-full sm:w-1/2 md:w-1/3 lg:w-1/4">
              <label className="text-sm">Phone Number</label>
              <div className="border border-gray-300 rounded-md">
                <PhoneInput
                  required
                  placeholder="Enter your phone number"
                  className="w-full p-2 text-sm border-none rounded-md outline-none focus:outline-none "
                  international
                  defaultCountry="GB"
                  value={phone}
                  onChange={handleChangePhoneNumber}
                />
              </div>
              {formData.errors.phone && (
                <p className="mt-1 text-xs text-red-500">
                  {formData.errors.phone}
                </p>
              )}
            </div>
            <div className="w-full sm:w-1/2 md:w-1/3 lg:w-1/4">
              <label className="text-sm">Address Line 1</label>
              <div className="p-2 border border-gray-300 rounded-md ">
                <input
                  className="w-full outline-none"
                  label={"Address line 1 *"}
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Address Line 1"
                  required
                />
              </div>
              {formData.errors.location && (
                <p className="mt-1 text-xs text-red-500">
                  {formData.errors.location}
                </p>
              )}
            </div>
            <div className="w-full sm:w-1/2 md:w-1/3 lg:w-1/4">
              <label className="text-sm">Postcode</label>
              <div className="p-2 border border-gray-300 rounded-md">
                <input
                  className="w-full outline-none"
                  label={"Postcode"}
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleChange}
                  placeholder="Postcode "
                />
              </div>
              {formData.errors.postcode && (
                <p className="mt-1 text-xs text-red-500">
                  {formData.errors.postcode}
                </p>
              )}
            </div>

            <div className="w-full m-3 sm:w-1/2 md:w-1/3 lg:w-1/4">
              <button
                style={{
                  backgroundColor: selectedStore?.theme.backgroundColor,
                }}
                onClick={handleSubmit}
                className="w-full px-4 py-2 text-white rounded-md hover:bg-lighterPurple focus:outline-none focus:ring focus:border-theme-color"
              >
                Save Changes
              </button>
            </div>
          </div>
          {isModalOpen && (
            <ImageModal
              modalData={{
                ...modalData,
                formData, 
                phone
              }}
              handleModalToggle={handleModalToggle}
              isOpen={isModalOpen}
              checkProfileUpdate={checkProfileUpdate}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileUpdate;
